'use client'

import {
    DunningPolicy,
    OverdueMode,
    getDunningPolicy,
    updateDunningPolicy,
} from '@/lib/api/master/master-settings-api'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdNotificationsActive,
    MdSave,
    MdWarningAmber,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const DEFAULT_POLICY: DunningPolicy = {
    preDueEnabled: true,
    preDueDays: [3, 1, 0],
    overdueMode: 'UNTIL_DAYS',
    overdueMaxDays: 30,
    overdueIntervalDays: 7,
    overdueStartDate: '2026-05-01',
}

const FOREVER_MAX_DAYS = 180

export default function DunningSettingsPage() {
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [policy, setPolicy] = useState<DunningPolicy>(DEFAULT_POLICY)
    // preDueDays editado como texto separado por vírgula para facilitar a edição.
    const [preDueDaysText, setPreDueDaysText] = useState('3, 1, 0')

    useEffect(() => {
        load()
    }, [])

    const load = async () => {
        setLoading(true)
        try {
            const res = await getDunningPolicy()
            if (res.success && res.result) {
                setPolicy(res.result)
                setPreDueDaysText((res.result.preDueDays || []).join(', '))
            }
        } catch (error) {
            console.error('Erro ao carregar política de cobrança:', error)
            toast.error('Erro ao carregar política de cobrança')
        } finally {
            setLoading(false)
        }
    }

    const parsePreDueDays = (text: string): number[] => {
        return Array.from(
            new Set(
                text
                    .split(',')
                    .map((p) => parseInt(p.trim(), 10))
                    .filter((n) => Number.isFinite(n) && n >= 0 && n <= 365)
            )
        ).sort((a, b) => b - a)
    }

    const handleSave = async () => {
        const preDueDays = parsePreDueDays(preDueDaysText)

        if (policy.preDueEnabled && preDueDays.length === 0) {
            toast.error('Informe ao menos um dia válido para os avisos a vencer')
            return
        }
        if (policy.overdueMode === 'UNTIL_DAYS' && policy.overdueMaxDays < 1) {
            toast.error('O teto de dias de inadimplência deve ser maior que zero')
            return
        }
        if (policy.overdueIntervalDays < 1) {
            toast.error('A cadência de reenvio deve ser de pelo menos 1 dia')
            return
        }

        setSaving(true)
        try {
            const payload: DunningPolicy = { ...policy, preDueDays }
            const res = await updateDunningPolicy(payload)
            if (res.success && res.result) {
                setPolicy(res.result)
                setPreDueDaysText((res.result.preDueDays || []).join(', '))
                toast.success('Política de cobrança salva com sucesso')
            } else {
                toast.error(res.error || 'Erro ao salvar política de cobrança')
            }
        } catch (error) {
            console.error('Erro ao salvar política de cobrança:', error)
            toast.error('Erro ao salvar política de cobrança')
        } finally {
            setSaving(false)
        }
    }

    const setField = <K extends keyof DunningPolicy>(
        key: K,
        value: DunningPolicy[K]
    ) => {
        setPolicy((prev) => ({ ...prev, [key]: value }))
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <span className="loading loading-spinner loading-lg text-secondary"></span>
                <p className="text-base-content/60 text-sm">
                    Carregando configuração...
                </p>
            </div>
        )
    }

    const effectiveMaxDays =
        policy.overdueMode === 'FOREVER'
            ? FOREVER_MAX_DAYS
            : policy.overdueMaxDays

    return (
        <div className="flex flex-col gap-6 p-4 max-w-3xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate(-1)}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                        <MdNotificationsActive className="text-2xl text-secondary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Política de Cobrança
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Avisos de vencimento e cobrança de inadimplência
                        </p>
                    </div>
                </div>
            </div>

            {/* Avisos a vencer */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-2">Avisos antes do vencimento</h2>

                    <label className="label cursor-pointer justify-start gap-3">
                        <input
                            type="checkbox"
                            className="toggle toggle-secondary"
                            checked={policy.preDueEnabled}
                            onChange={(e) =>
                                setField('preDueEnabled', e.target.checked)
                            }
                        />
                        <span className="label-text font-medium">
                            Enviar avisos antes do vencimento
                        </span>
                    </label>

                    {policy.preDueEnabled && (
                        <div className="form-control w-full mt-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Dias antes do vencimento
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full max-w-xs focus:input-primary"
                                placeholder="3, 1, 0"
                                value={preDueDaysText}
                                onChange={(e) => setPreDueDaysText(e.target.value)}
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/60">
                                    Separe por vírgula. Ex: "3, 1, 0" envia 3 dias antes,
                                    1 dia antes e no dia do vencimento.
                                </span>
                            </label>
                        </div>
                    )}
                </div>
            </div>

            {/* Inadimplência */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-2">
                        Cobrança de inadimplência
                    </h2>

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">
                                Estratégia após o vencimento
                            </span>
                        </label>
                        <select
                            className="select select-bordered w-full max-w-md focus:select-primary"
                            value={policy.overdueMode}
                            onChange={(e) =>
                                setField('overdueMode', e.target.value as OverdueMode)
                            }
                        >
                            <option value="OFF">
                                Não cobrar inadimplência (apenas avisos a vencer)
                            </option>
                            <option value="UNTIL_DAYS">
                                Cobrar até X dias após o vencimento
                            </option>
                            <option value="FOREVER">
                                Cobrar sempre (limitado a 6 meses por cobrança)
                            </option>
                        </select>
                    </div>

                    {policy.overdueMode !== 'OFF' && (
                        <>
                            {policy.overdueMode === 'UNTIL_DAYS' && (
                                <div className="form-control w-full mt-4">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Cobrar até quantos dias após o vencimento
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        min={1}
                                        className="input input-bordered w-full max-w-xs focus:input-primary"
                                        value={policy.overdueMaxDays}
                                        onChange={(e) =>
                                            setField(
                                                'overdueMaxDays',
                                                parseInt(e.target.value, 10) || 0
                                            )
                                        }
                                    />
                                </div>
                            )}

                            <div className="form-control w-full mt-4">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Cadência de reenvio (dias)
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    className="input input-bordered w-full max-w-xs focus:input-primary"
                                    value={policy.overdueIntervalDays}
                                    onChange={(e) =>
                                        setField(
                                            'overdueIntervalDays',
                                            parseInt(e.target.value, 10) || 0
                                        )
                                    }
                                />
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        Reenvia a cobrança de inadimplência a cada N dias
                                        (não todo dia).
                                    </span>
                                </label>
                            </div>

                            <div className="form-control w-full mt-4">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Cobrar somente vencimentos a partir de
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full max-w-xs focus:input-primary"
                                    value={policy.overdueStartDate}
                                    onChange={(e) =>
                                        setField('overdueStartDate', e.target.value)
                                    }
                                />
                            </div>

                            <div className="alert alert-warning mt-4">
                                <MdWarningAmber className="text-xl" />
                                <span className="text-sm">
                                    Cobranças vencidas antes de{' '}
                                    <strong>{policy.overdueStartDate}</strong> nunca
                                    serão cobradas (protege dados históricos). Cada
                                    cobrança será cobrada por no máximo{' '}
                                    <strong>{effectiveMaxDays} dias</strong> após o
                                    vencimento.
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="btn btn-ghost"
                    disabled={saving}
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    className="btn btn-secondary gap-2"
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <span className="loading loading-spinner loading-sm"></span>
                            Salvando...
                        </>
                    ) : (
                        <>
                            <MdSave className="text-lg" />
                            Salvar
                        </>
                    )}
                </button>
            </div>
        </div>
    )
}
